import { z } from 'zod';
// `CalendarDate` đã có ở `project.ts` — dùng lại chứ không khai bản thứ hai:
// hai biểu thức chính quy cho cùng một định dạng ngày là hai chỗ để lệch nhau.
import { OrgRole } from './auth.js';
import { CalendarDate } from './project.js';
import { WorkShift, minutesFromTimeOfDay } from './work-schedule.js';
/**
 * Vắng mặt: nghỉ phép của một người, và ngày nghỉ lễ của cả tổ chức — APM-1311.
 *
 * ## Vì sao nghỉ nửa ngày KHÔNG có cột `is_half_day`
 *
 * `APM-1310a` đã mô hình hoá một ngày làm việc thành **danh sách ca**, nên
 * "nghỉ buổi sáng" là bỏ ca sáng khỏi ngày đó — trần năng lực tự ra đúng phần
 * còn lại. Một hệ số `0.5` sẽ sai ngay với người có ca sáng 3 giờ và ca chiều 5
 * giờ: nghỉ sáng của họ không phải mất một nửa, mà là mất 3/8.
 *
 * Nên một đợt nghỉ mang **một khoảng giờ tuỳ chọn**: rỗng là nghỉ cả ngày, có
 * thì trừ đúng phần giao với từng ca. Cách này diễn đạt được cả "nghỉ sáng",
 * "về sớm từ 15:00" và "đi khám 10:00–11:30" mà không thêm khái niệm nào.
 *
 * ## Vì sao khoảng giờ chỉ dùng cho đợt nghỉ MỘT ngày
 *
 * "Từ thứ Hai đến thứ Tư, 08:00–12:00" đọc được hai kiểu: mỗi ngày nghỉ buổi
 * sáng, hay liền một mạch từ 08:00 thứ Hai tới 12:00 thứ Tư. Hai cách hiểu cho
 * ra hai trần năng lực khác nhau, và người khai không có cách nào nói rõ mình
 * muốn cái nào. Nghỉ nhiều ngày mà chỉ nghỉ buổi sáng thì khai từng ngày —
 * dài hơn khi nhập, nhưng không ai đọc sai về sau.
 */
/** Loại vắng mặt. Chỉ để hiển thị: cả ba trừ vào lịch y như nhau. */
export const AbsenceKind = z.enum(['leave', 'sick', 'other']);
/** Khoảng giờ nghỉ trong một ngày. Cùng hình dạng với một ca làm việc. */
export const AbsenceWindow = WorkShift;
/** Trần một đợt nghỉ: một năm. Dài hơn thì đó là nghỉ việc (`APM-1311b`). */
export const MAX_ABSENCE_DAYS = 366;
export const DeclareAbsenceInput = z
    .object({
    fromDate: CalendarDate,
    toDate: CalendarDate,
    kind: AbsenceKind,
    /** Rỗng = nghỉ cả ngày. Chỉ dùng được khi `fromDate` trùng `toDate`. */
    window: AbsenceWindow.nullable().default(null),
    note: z.string().max(500).trim().optional(),
    /**
     * Mã đơn — APM-ORG-f7. Nhiều ngày khai trong **một lượt gửi** truyền cùng
     * một mã, để bảng gộp chúng thành một dòng.
     *
     * Client sinh: mỗi lượt gửi là N lời gọi (một ngày một lời), nên server
     * không có chỗ nào nhìn thấy "cả lượt" để tự sinh mã.
     */
    requestId: z.uuid().optional(),
})
    .superRefine((value, ctx) => {
    if (value.toDate < value.fromDate) {
        ctx.addIssue({ code: 'custom', message: 'Ngày kết thúc phải sau hoặc bằng ngày bắt đầu' });
    }
    if (daysBetweenInclusive(value.fromDate, value.toDate) > MAX_ABSENCE_DAYS) {
        ctx.addIssue({ code: 'custom', message: 'Một đợt nghỉ không dài quá một năm' });
    }
    if (value.window !== null) {
        if (value.fromDate !== value.toDate) {
            ctx.addIssue({
                code: 'custom',
                message: 'Nghỉ theo giờ chỉ khai được cho một ngày. Nhiều ngày thì khai từng ngày.',
            });
        }
        if (minutesFromTimeOfDay(value.window.end) <= minutesFromTimeOfDay(value.window.start)) {
            ctx.addIssue({ code: 'custom', message: 'Giờ kết thúc phải sau giờ bắt đầu' });
        }
    }
});
export const AbsenceView = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    fromDate: CalendarDate,
    toDate: CalendarDate,
    kind: AbsenceKind,
    window: AbsenceWindow.nullable(),
    note: z.string().nullable(),
    /**
     * Lúc khai.
     *
     * Có mặt vì Replay: "tua về trước lúc khai thì vẫn thấy năng lực cũ" cần biết
     * đợt nghỉ này **được biết từ khi nào**, chứ không chỉ nó rơi vào ngày nào.
     */
    declaredAt: z.iso.datetime(),
    /** Tổng giờ đợt nghỉ này trừ khỏi lịch của người đó. */
    hours: z.number().nonnegative(),
});
export const AbsencesView = z.object({
    userId: z.uuid(),
    absences: z.array(AbsenceView),
});
// ── Ngày nghỉ lễ của tổ chức ────────────────────────────────────────────────
export const HolidayView = z.object({
    id: z.uuid(),
    date: CalendarDate,
    name: z.string(),
});
export const HolidaysView = z.object({ holidays: z.array(HolidayView) });
export const AddHolidayInput = z.object({
    date: CalendarDate,
    name: z.string().trim().min(1, 'Ngày lễ cần một cái tên').max(200),
});
/** Cả ngày, dùng cho nghỉ trọn ngày và cho ngày nghỉ lễ. */
export const FULL_DAY = { startMinute: 0, endMinute: 24 * 60 };
export function minuteWindowOf(window) {
    return window === null
        ? FULL_DAY
        : {
            startMinute: minutesFromTimeOfDay(window.start),
            endMinute: minutesFromTimeOfDay(window.end),
        };
}
/**
 * Số giờ còn lại của một ngày sau khi trừ các khoảng vắng mặt.
 *
 * Trừ theo **phần giao với từng ca**, không theo hệ số: một người làm ca sáng 3
 * giờ và ca chiều 5 giờ mà nghỉ sáng thì còn 5 giờ, không phải 4.
 *
 * ⚠️ Không bao giờ trả số âm, và các khoảng vắng mặt chồng nhau **không** bị
 * trừ hai lần: hai đợt nghỉ cùng phủ buổi sáng sẽ làm trần âm nếu cứ trừ dồn,
 * và một trần âm khiến mọi phép so "quá tải chưa" trả lời ngược.
 */
export function hoursAfterOff(shifts, off) {
    let minutes = 0;
    for (const shift of shifts) {
        const start = minutesFromTimeOfDay(shift.start);
        const end = minutesFromTimeOfDay(shift.end);
        let worked = Math.max(0, end - start);
        if (worked === 0)
            continue;
        // Gộp các khoảng nghỉ **giao với ca này** thành hợp rồi mới trừ một lần.
        const cuts = off
            .map((window) => ({
            from: Math.max(start, window.startMinute),
            to: Math.min(end, window.endMinute),
        }))
            .filter((cut) => cut.to > cut.from)
            .sort((a, b) => a.from - b.from);
        let cursor = -1;
        for (const cut of cuts) {
            const from = Math.max(cut.from, cursor);
            if (cut.to <= from)
                continue;
            worked -= cut.to - from;
            cursor = cut.to;
        }
        minutes += Math.max(0, worked);
    }
    return Math.round((minutes / 60) * 100) / 100;
}
/** Mọi ngày trong khoảng đóng `[from, to]`, dạng `YYYY-MM-DD`. */
export function calendarDatesBetween(from, to) {
    const dates = [];
    const cursor = new Date(`${from}T00:00:00Z`);
    const last = new Date(`${to}T00:00:00Z`).getTime();
    while (cursor.getTime() <= last) {
        dates.push(cursor.toISOString().slice(0, 10));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return dates;
}
export function daysBetweenInclusive(from, to) {
    const ms = new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime();
    return Math.floor(ms / 86_400_000) + 1;
}
/** Cộng (hoặc trừ) ngày vào một `CalendarDate`, theo UTC. */
export function addCalendarDays(date, days) {
    return new Date(new Date(`${date}T00:00:00Z`).getTime() + days * 86_400_000)
        .toISOString()
        .slice(0, 10);
}
/** `0` = Chủ nhật … `6` = thứ Bảy, đúng `Date.getUTCDay()`. */
export function weekdayOf(date) {
    return new Date(`${date}T00:00:00Z`).getUTCDay();
}
/**
 * Số giờ một đợt nghỉ trừ khỏi lịch của người đó.
 *
 * Dùng ở cả hai phía: form khai nghỉ hiện "đợt này trừ 32 giờ" ngay lúc chọn
 * ngày, còn server trả đúng con số ấy trong `AbsenceView`. Một phép tính, một
 * chỗ viết — cùng lý do như `weeklyHours` của lịch gốc.
 *
 * Ngày nghỉ lễ **không** trừ thêm ở đây: nghỉ phép trùng ngày lễ không tốn thêm
 * giờ nào, nhưng con số này nói về *đợt nghỉ* nên nó tính theo lịch gốc. Trần
 * năng lực thật thì hợp cả hai khoảng lại — xem `hoursAfterOff`.
 */
export function absenceHours(days, from, to, window) {
    const off = [minuteWindowOf(window)];
    let hours = 0;
    for (const date of calendarDatesBetween(from, to)) {
        const shifts = days.find((day) => day.weekday === weekdayOf(date))?.shifts ?? [];
        hours += hoursAfterOff(shifts, []) - hoursAfterOff(shifts, off);
    }
    return Math.round(hours * 100) / 100;
}
/**
 * Trạng thái duyệt của một đợt nghỉ — APM-ORG-f1.
 *
 * Suy từ `approved_at`, không lưu thành cột riêng: hai nguồn sự thật cho cùng
 * một sự việc sẽ lệch nhau ở đúng lúc ai đó quên cập nhật một trong hai.
 */
export const AbsenceApproval = z.enum(['pending', 'approved']);
/**
 * Đợt nghỉ đang ở đâu so với hôm nay — APM-ORG-f1.
 *
 * Cũng suy ra, từ `from_date`/`to_date` so với ngày hiện tại. Lưu thành cột thì
 * nó **sai mỗi nửa đêm** cho tới khi có job chạy qua sửa lại.
 */
export const AbsenceTiming = z.enum(['past', 'current', 'upcoming']);
/** Một dòng trong bảng nghỉ phép toàn tổ chức — APM-ORG-f1. */
export const OrgAbsenceRow = z.object({
    id: z.uuid(),
    /** `null` với hàng khai trước APM-ORG-f7 — chúng đứng một mình. */
    requestId: z.uuid().nullable(),
    userId: z.uuid(),
    displayName: z.string(),
    orgRole: OrgRole,
    /** Số dự án người này đang tham gia — để biết đợt nghỉ ảnh hưởng tới đâu. */
    projectCount: z.number().int().nonnegative(),
    fromDate: CalendarDate,
    toDate: CalendarDate,
    kind: AbsenceKind,
    /** `null` = nghỉ cả ngày. Có giá trị = nghỉ đúng khung giờ đó trong một ngày. */
    window: AbsenceWindow.nullable(),
    note: z.string().nullable(),
    approval: AbsenceApproval,
    timing: AbsenceTiming,
});
/**
 * Một đợt nghỉ **chồng lên task** của chính người ấy trong một dự án —
 * APM-ORG-g1.
 *
 * Tín hiệu **suy ra**, không phải một bảng: mỗi lượt gọi tính lại từ
 * `work_absence` và `task`. Ghi thành bảng thì phải có người dọn khi đợt nghỉ
 * bị rút, khi task đổi ngày, khi người đó rời dự án — ba đường làm nó lệch, và
 * một cảnh báo lệch còn tệ hơn không có cảnh báo.
 */
export const AbsenceImpactRow = z.object({
    absenceId: z.uuid(),
    /** Gộp nhiều ngày của **một đơn** — APM-ORG-g1. `null` với hàng cũ. */
    requestId: z.uuid().nullable(),
    userId: z.uuid(),
    displayName: z.string(),
    /**
     * Email và chức vụ đi kèm tên — APM-ORG-g3.
     *
     * Tên hiển thị một mình không đủ phân biệt: bộ demo có "Quản trị viên
     * (DEMO)", một chuỗi đọc như chức danh chứ không như một con người, và ở tổ
     * chức thật thì hai người trùng tên là chuyện thường.
     */
    email: z.string(),
    orgRole: OrgRole,
    fromDate: CalendarDate,
    toDate: CalendarDate,
    kind: AbsenceKind,
    approval: AbsenceApproval,
    /** Luôn có ít nhất một phần tử — đợt nghỉ không đụng task nào thì không trả về. */
    tasks: z.array(z.object({
        taskId: z.uuid(),
        number: z.number().int().positive(),
        title: z.string(),
        startDate: CalendarDate,
        dueDate: CalendarDate,
    })),
});
//# sourceMappingURL=absence.js.map